"use client";

import dynamic from "next/dynamic";
import HeroSection from "@/components/HeroSection";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import type { Category } from "@/lib/products";
import { CtaBlock } from "@/components/CtaBlock";

const WhyChooseKasai = dynamic(() => import("@/components/WhyChooseKasai"));
const QuoteSection = dynamic(() =>
  import("@/components/home/QuoteSection").then((mod) => mod.QuoteSection),
);

type HomePageProps = {
  categoryModelCounts: Record<string, number>;
  categories: Category[];
};

const HomePage = ({ categoryModelCounts, categories }: HomePageProps) => (
  <>
    <HeroSection />

    {/* Exclusive Partner — Engirise */}
    <section className="w-full bg-[#F9FAFB] py-12 sm:py-[54px] overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-10 md:px-[80px]">
        <div className="space-y-8">
          {/* Header */}
          <div className="text-center space-y-3 md:space-y-4">
            <h2 className="text-primary font-bold text-sm tracking-[0.3em] uppercase">
              Exclusive Partner
            </h2>
            <p className="font-poppins font-medium text-para text-tsavo-gray-dark max-w-[781px] mx-auto leading-relaxed">
              Kasai Equipment proudly partners with Engirise to deliver reliable heavy equipment solutions built for African industries and demanding job sites.
            </p>
          </div>

          {/* ENGIRISE Logo with Large Background Text */}
          <div className="relative w-full py-8 md:py-12 h-full">
            {/* Large Background Text - Full Width */}
            <div className="absolute inset-0 flex items-center justify-center overflow-hidden h-[8rem] md:h-[12rem]">
              <h3
                className="font-poppins font-bold text-[80px] sm:text-[150px] md:text-[250px] lg:text-[250px] leading-none select-none pointer-events-none whitespace-nowrap opacity-10" style={{
                  background: 'linear-gradient(180deg, #000 10%, #fff 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',

                }}
              >
                ENGIRISE
              </h3>
            </div>

            {/* Centered Logo */}
            <div className="relative z-10 flex justify-center">
              <div className="px-6 md:px-8">
                <Image
                  src="/assets/EngiriseLogo2.svg"
                  alt="Engirise logo"
                  width={220}
                  height={64}
                  className="h-10 sm:h-14 md:h-16 w-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Featured Categories */}
    <section className="py-16 sm:py-20 md:py-24 bg-muted/50">
      <div className="container mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10 sm:mb-16">
          <span className="text-primary font-bold text-sm tracking-[0.3em] uppercase">Our Fleet</span>
          <h2 className="font-display text-[24px] font-bold md:text-4xl mt-3 text-foreground">Heavy Equipment</h2>
          <p className="text-muted-foreground mt-4 max-w-xl mx-auto">Premium Engirise machinery backed by Kasai's expert service and support network.</p>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.filter(c => c.active).map((cat, i) => (
            <motion.div
              key={cat.slug}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              whileHover={{ y: -12, scale: 1.02 }}
            >
              <Link href={`/products/${cat.slug}`} className="group block glass rounded-2xl overflow-hidden cursor-pointer transition-shadow duration-300 hover:glow-primary">
                <div className="relative overflow-hidden">
                  <Image
                    src={cat.image}
                    alt={`${cat.name} heavy equipment from Kasai Equipment`}
                    width={640}
                    height={420}
                    sizes="(min-width: 1024px) 25vw, (min-width: 768px) 50vw, 100vw"
                    className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <span className="absolute top-3 left-3 bg-accent text-accent-foreground text-xs font-bold px-3 py-1 rounded-full">
                    {categoryModelCounts[cat.slug] ?? 0} Models
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-display text-base text-foreground">{cat.name}</h3>
                  <div className="mt-4 flex items-center gap-2 text-primary font-semibold text-sm group-hover:gap-3 transition-all">
                    View Models
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link href="/products" className="inline-block bg-primary text-primary-foreground px-8 py-4 rounded-lg font-bold text-base hover:glow-primary transition-all duration-300 hover:-translate-y-1">
            View All Products
          </Link>
        </div>
      </div>
    </section>

    {/* Why Choose Kasai — Industrial Grid */}
    <WhyChooseKasai />


    {/* Industries We Serve — Editorial split-rail */}
    <section className="relative overflow-hidden bg-background py-16 sm:py-20 md:py-28">
      {/* Subtle grid backdrop */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="container mx-auto px-4 relative">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-end mb-10 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <span className="text-primary font-bold text-sm tracking-[0.3em] uppercase">
              Industries We Serve
            </span>
            <h2 className="font-display text-3xl sm:text-4xl mt-4 text-foreground leading-[0.95]">
              From Mines 
              <span className="text-primary"> to Markets.</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-5 text-base sm:text-lg text-muted-foreground leading-relaxed lg:pb-3"
          >
            Wherever the work happens — open pits, urban builds, fertile fields, or busy ports —
            Kasai puts the right Engirise machine on the ground, with the parts and service that
            keep it earning.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-border rounded-[28px] overflow-hidden border border-border">
          {[
            {
              num: "01",
              name: "Construction",
              desc: "Loaders, excavators, and graders engineered for high-tempo build sites.",
              icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-10 h-10">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M5 21V10l7-5 7 5v11M9 21v-6h6v6" />
                </svg>
              ),
            },
            {
              num: "02",
              name: "Mining",
              desc: "Heavy-duty haulers and rigid frames built to survive abrasive ore environments.",
              icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-10 h-10">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 3l-1.5 4M10 3l1.5 4M4 11h16l-2 10H6L4 11zM8 11l1-4h6l1 4" />
                </svg>
              ),
            },
            {
              num: "03",
              name: "Agriculture",
              desc: "Tractors and field equipment tuned for African soil, season after season.",
              icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-10 h-10">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 17a4 4 0 108 0 4 4 0 00-8 0zM15 14a3 3 0 106 0 3 3 0 00-6 0zM11 17h4M14 8l4-3M14 8v6" />
                </svg>
              ),
            },
            {
              num: "04",
              name: "Infrastructure",
              desc: "Road, rail, and utility projects backed by fleet-grade reliability and uptime.",
              icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-10 h-10">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18M5 12V8a2 2 0 012-2h10a2 2 0 012 2v4M8 12v8M16 12v8M3 20h18" />
                </svg>
              ),
            },
            {
              num: "05",
              name: "Material Handling",
              desc: "Forklifts and yard equipment that keep ports, depots, and warehouses flowing.",
              icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-10 h-10">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v13h12M16 17a2 2 0 100 4 2 2 0 000-4zM7 17a2 2 0 100 4 2 2 0 000-4zM16 17V7h4l2 4v6h-2" />
                </svg>
              ),
            },
          ].map((ind, i) => (
            <motion.div
              key={ind.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group relative bg-card p-6 sm:p-8 transition-colors duration-300 hover:bg-foreground cursor-default"
            >
              <span className="font-display text-xs tracking-[0.3em] text-muted-foreground group-hover:text-primary transition-colors">
                {ind.num}
              </span>
              <div className="mt-8 text-foreground group-hover:text-primary transition-colors">
                {ind.icon}
              </div>
              <h3 className="font-display text-base mt-6 text-foreground group-hover:text-card transition-colors">
                {ind.name}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground group-hover:text-card/70 transition-colors">
                {ind.desc}
              </p>
              <div className="mt-6 h-px w-10 bg-border group-hover:bg-primary group-hover:w-16 transition-all duration-300" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* About Us */}
    <section className="relative overflow-hidden bg-background py-16 sm:py-20 md:py-28 border-y border-[oklch(0.92_0.008_255)]">
      {/* Subtle background accents */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.04]"

      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[26rem] w-[26rem] rounded-full bg-primary/10 blur-[120px]"
      />

      <div className="container mx-auto px-4 relative">
        {/* Centered header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center"
        >
          <span className="text-primary font-bold text-sm tracking-widest uppercase">
            About Us
          </span>
          <h2 className="font-display text-3xl sm:text-4xl mt-5 leading-[0.95] text-foreground">
            Built on Trust.
            <br />
            <span
              className="text-primary"
              style={{ WebkitTextStroke: "1.5px hsl(var(--primary))" }}
            >
              Driven by Machinery.
            </span>
          </h2>
          <p className="mt-6 sm:mt-8 text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed">
            We supply construction, mining, and industrial heavy machinery backed by strong local support.
          </p>
        </motion.div>

        {/* Centered credential grid */}
        <div className="mt-10 sm:mt-16 grid gap-6 md:grid-cols-3 max-w-6xl mx-auto">
          {[
            {
              num: "01",
              title: "Authorized Distribution Partner",
              desc: "Official Engirise dealer for the region — direct factory access.",
            },
            {
              num: "02",
              title: "Local Service teams based in congo",
              desc: "Engineers based in DR Congo, ready to mobilize across your sites.",
            },
            {
              num: "03",
              title: "Certified pre-owned equipment sale",
              desc: "Factory-fresh units and inspected pre-owned equipment for sale.",
            },
          ].map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className="group relative flex flex-col items-center text-center rounded-2xl border border-border bg-card p-6 sm:p-8 transition-all duration-300 hover:border-primary/40 hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="flex items-center justify-center h-14 w-14 rounded-full border border-primary/40 bg-primary/10 text-primary font-display text-base tracking-widest transition-all group-hover:bg-primary group-hover:text-primary-foreground">
                {item.num}
              </div>
              <h3 className="font-display text-base text-foreground mt-5">
                {item.title}
              </h3>
              <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
                {item.desc}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Centered CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-14 flex justify-center"
        >
          <Link
            href="/about"
            className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 rounded-lg font-bold text-base hover:glow-primary transition-all duration-300 hover:-translate-y-1"
          >
            Learn More About Us
            <svg
              className="w-5 h-5 transition-transform group-hover:translate-x-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </motion.div>
      </div>
    </section>



    {/* CTA */}
    <CtaBlock
      title="Ready to move your project forward?"
      text="Contact our team for equipment inquiries, service requests, or to schedule a site visit."
      primary={{ to: "/contact", label: "Get a Quote" }}
    />


  </>
);

export default HomePage;
